import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  exportManuscriptMarkdown,
  listManuscriptSnapshots,
  replaceInManuscript,
  saveManuscriptSnapshot,
  splitTextAt,
  mergeTexts,
  subscribeWorkspace,
  toSortedArray,
} from "../../services/workspaceService";
import WorkspacePathPicker from "./WorkspacePathPicker";

const btn = {
  padding: "10px 14px",
  minHeight: 44,
  borderRadius: 8,
  border: "1px solid rgba(212,175,55,0.45)",
  background: "rgba(212,175,55,0.12)",
  color: "#e8e0d2",
  fontSize: "0.95rem",
  cursor: "pointer",
};
const btnDisabled = { ...btn, opacity: 0.45, cursor: "default" };
const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  minHeight: 44,
  borderRadius: 8,
  border: "1px solid rgba(232,224,210,0.25)",
  background: "rgba(255,255,255,0.06)",
  color: "#e8e0d2",
  fontSize: "1rem",
};
const card = {
  border: "1px solid rgba(232,224,210,0.14)",
  borderRadius: 12,
  padding: 16,
  margin: "16px 0",
  background: "rgba(255,255,255,0.03)",
};

export default function LongformTools() {
  const [workspace, setWorkspace] = useState(null);
  const [path, setPath] = useState({ deskId: "", drawerId: "", notebookId: "" });
  const [markdown, setMarkdown] = useState("");
  const [search, setSearch] = useState("");
  const [replacement, setReplacement] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [splitId, setSplitId] = useState("");
  const [mergeIds, setMergeIds] = useState([]); // ordered pair
  const [offset, setOffset] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribeWorkspace(setWorkspace), []);

  const refreshSnapshots = useCallback(
    () => listManuscriptSnapshots().then(setSnapshots).catch((e) => setStatus(`No se pudieron leer los snapshots: ${e.message}`)),
    []
  );
  useEffect(() => {
    refreshSnapshots();
  }, [refreshSnapshots]);

  // Scenes visible in scope: whole workspace, or just the picked notebook.
  useEffect(() => {
    if (!workspace) return;
    let list = [];
    for (const desk of toSortedArray(workspace.desks) || []) {
      if (path.deskId && desk.id !== path.deskId) continue;
      for (const drawer of toSortedArray(desk.drawers) || []) {
        if (path.drawerId && drawer.id !== path.drawerId) continue;
        for (const nb of toSortedArray(drawer.notebooks) || []) {
          if (path.notebookId && nb.id !== path.notebookId) continue;
          for (const t of toSortedArray(nb.texts) || []) {
            list.push({ ...t, deskId: desk.id, drawerId: drawer.id, notebookId: nb.id });
          }
        }
      }
    }
    list.sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")));
    setScenes(list);
  }, [workspace, path.deskId, path.drawerId, path.notebookId]);

  const scopeArgs = useMemo(
    () => ({
      deskId: path.deskId || undefined,
      drawerId: path.drawerId || undefined,
      notebookId: path.notebookId || undefined,
    }),
    [path]
  );

  const run = (fn, okMsg) => async (...args) => {
    if (busy) return;
    setBusy(true);
    setStatus("Trabajando…");
    try {
      const result = await fn(...args);
      setStatus(typeof okMsg === "function" ? okMsg(result) : okMsg);
      return result;
    } catch (e) {
      setStatus(`Error: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const compile = run(async () => {
    const md = await exportManuscriptMarkdown(scopeArgs);
    setMarkdown(md);
    return md;
  }, (md) => `Manuscrito compilado (${md.split("\n").length} líneas).`);

  const download = () => {
    if (!markdown) return;
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "manuscrito.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  const snapshot = run(async () => {
    await saveManuscriptSnapshot(`Manual · ${new Date().toLocaleString()}`);
    await refreshSnapshots();
  }, "Snapshot guardado.");
  const replaceAll = run(async () => {
    await saveManuscriptSnapshot(`Antes de reemplazar «${search.slice(0, 40)}» · ${new Date().toLocaleString()}`);
    const result = await replaceInManuscript(search, replacement, scopeArgs);
    await refreshSnapshots();
    return result;
  }, (r) => `${r.count} nota(s) actualizadas. Snapshot previo guardado.`);

  const splitScene = run(async () => {
    const scene = scenes.find((s) => s.id === splitId);
    if (!scene) throw new Error("Elegí una escena para dividir.");
    await saveManuscriptSnapshot(`Antes de dividir «${scene.title}» · ${new Date().toLocaleString()}`);
    await splitTextAt(scene.deskId, scene.drawerId, scene.notebookId, scene.id, Number(offset));
    await refreshSnapshots();
  }, "Escena dividida. La segunda mitad quedó como escena nueva.");

  const mergeScenes = run(async () => {
    if (mergeIds.length !== 2) throw new Error("Elegí dos escenas para unir.");
    const [a, b] = mergeIds.map((id) => scenes.find((s) => s.id === id));
    if (!a || !b) throw new Error("Escenas no encontradas.");
    if (a.deskId !== b.deskId || a.drawerId !== b.drawerId || a.notebookId !== b.notebookId)
      throw new Error("Las dos escenas deben estar en el mismo cuaderno.");
    await saveManuscriptSnapshot(`Antes de unir «${a.title}» + «${b.title}» · ${new Date().toLocaleString()}`);
    await mergeTexts(a.deskId, a.drawerId, a.notebookId, a.id, b.id);
    setMergeIds([]);
    await refreshSnapshots();
  }, "Escenas unidas.");

  const toggleMerge = (id) =>
    setMergeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 2 ? [...prev, id] : [prev[1], id]));

  return (
    <main style={{ maxWidth: 760, margin: "0 auto", padding: "16px 16px 80px", color: "#e8e0d2" }}>
      <h1 style={{ fontSize: "1.3rem", margin: "8px 0 4px" }}>Herramientas del manuscrito</h1>
      <p style={{ opacity: 0.75, margin: 0 }}>
        Compilá, protegé y reorganizá tu libro. Todo cambio grande guarda un snapshot antes.
      </p>

      <div role="status" aria-live="polite" style={{ minHeight: 24, margin: "10px 0", color: "#D4AF37", fontSize: "0.9rem" }}>
        {busy ? "Trabajando…" : status}
      </div>

      <section style={card}>
        <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Ámbito</h2>
        <p style={{ opacity: 0.7, fontSize: "0.85rem", marginTop: 0 }}>
          Vacío = todo el workspace. Elegí el cuaderno de la novela para acotar.
        </p>
        <WorkspacePathPicker
          workspace={workspace}
          deskId={path.deskId}
          drawerId={path.drawerId}
          notebookId={path.notebookId}
          onChangePath={(p) => setPath({ deskId: p.deskId || "", drawerId: p.drawerId || "", notebookId: p.notebookId || "" })}
        />
        <p style={{ opacity: 0.6, fontSize: "0.85rem" }}>{scenes.length} texto(s) en el ámbito.</p>
      </section>

      <section style={card}>
        <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Compilar</h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={compile} disabled={busy} style={busy ? btnDisabled : btn}>Compilar a Markdown</button>
          <button onClick={download} disabled={!markdown} style={!markdown ? btnDisabled : btn}>Descargar manuscrito.md</button>
        </div>
        {markdown && (
          <textarea
            readOnly
            value={markdown}
            style={{ width: "100%", minHeight: 320, marginTop: 12, ...input, fontFamily: "monospace", fontSize: "0.85rem" }}
          />
        )}
      </section>

      <section style={card}>
        <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Snapshots</h2>
        <button onClick={snapshot} disabled={busy} style={busy ? btnDisabled : btn}>Guardar snapshot ahora</button>
        {snapshots.length > 0 && (
          <ul style={{ margin: "12px 0 0", paddingLeft: 18, opacity: 0.8, fontSize: "0.85rem" }}>
            {snapshots.slice(0, 8).map((s) => (
              <li key={s.id}>{s.label}</li>
            ))}
          </ul>
        )}
      </section>

      <section style={card}>
        <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Buscar y reemplazar</h2>
        <div style={{ display: "grid", gap: 8 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar en el ámbito" style={input} />
          <input value={replacement} onChange={(e) => setReplacement(e.target.value)} placeholder="Reemplazar por" style={input} />
          <button onClick={replaceAll} disabled={busy || !search.trim()} style={busy || !search.trim() ? btnDisabled : btn}>
            Guardar snapshot y reemplazar
          </button>
        </div>
      </section>

      <section style={card}>
        <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Dividir / unir escenas</h2>
        {scenes.length === 0 && <p style={{ opacity: 0.7 }}>No hay textos en el ámbito.</p>}
        <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 320, overflowY: "auto" }}>
          {scenes.map((s) => {
            const isSplit = splitId === s.id;
            const mergeIdx = mergeIds.indexOf(s.id);
            return (
              <li key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid rgba(232,224,210,0.08)" }}>
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.title || "(sin título)"}
                  {" "}
                  <span style={{ opacity: 0.55 }}>· {String(s.content || "").length} car.</span>
                </span>
                <button onClick={() => setSplitId(isSplit ? "" : s.id)} style={{ ...btn, minHeight: 36, padding: "4px 10px", ...(isSplit ? { background: "rgba(212,175,55,0.35)" } : {}) }}>
                  Dividir
                </button>
                <button onClick={() => toggleMerge(s.id)} style={{ ...btn, minHeight: 36, padding: "4px 10px", ...(mergeIdx >= 0 ? { background: "rgba(212,175,55,0.35)" } : {}) }}>
                  {mergeIdx >= 0 ? `Unir ${mergeIdx + 1}` : "Unir"}
                </button>
              </li>
            );
          })}
        </ul>
        {splitId && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
            <input
              value={offset}
              onChange={(e) => setOffset(e.target.value)}
              inputMode="numeric"
              placeholder="Corte en carácter nº"
              style={{ ...input, maxWidth: 220 }}
            />
            <button onClick={splitScene} disabled={busy || !offset} style={busy || !offset ? btnDisabled : btn}>Dividir escena</button>
          </div>
        )}
        {mergeIds.length === 2 && (
          <button onClick={mergeScenes} disabled={busy} style={{ ...btn, marginTop: 12 }}>
            Unir las dos escenas seleccionadas
          </button>
        )}
      </section>
    </main>
  );
}
